/**
 * Rules & Memory: what agents are told on every run (global → project → workstream). Agent and
 * brain proposals wait here (and in the Inbox) until promoted; each rule shows where it came
 * from and when an agent last cited it.
 */
import { useQueryClient } from '@tanstack/react-query'
import { Button, Chip, Sheet } from '@viko/transcript-ui'
import { BookMarked, Check, Plus, Undo2, X } from 'lucide-react'
import { useState } from 'react'
import {
  Ago,
  Empty,
  errorText,
  PageHead,
  SectionTitle,
  SkeletonRows,
  useToast,
} from '../components/ui.js'
import { useBackend } from '../lib/backend.js'
import { useProjects, useRules } from '../lib/queries.js'
import type { Rule } from '../lib/types.js'

/**
 * ia-shell: `projectId` fixes the scope (a project's Settings: its project and workstream rules);
 * `embedded` drops the page chrome (Settings › Rules).
 */
export function RulesPage({
  projectId: fixed,
  embedded,
}: {
  projectId?: string
  embedded?: boolean
} = {}) {
  const [picked, setProjectId] = useState<string | undefined>()
  const projectId = fixed ?? picked
  const projects = useProjects()
  const rules = useRules(projectId)
  const [adding, setAdding] = useState(false)
  const list = rules.data ?? []
  const groups: [string, Rule[]][] = [
    ['Proposed', list.filter((r) => r.status === 'proposed')],
    ['Active', list.filter((r) => r.status === 'active')],
    ['Retired', list.filter((r) => r.status === 'retired')],
  ]
  return (
    <div className={embedded || fixed ? 'min-w-0' : 'app-list flex-1'}>
      <div className={embedded || fixed ? 'max-w-[760px] pb-6' : 'mx-auto max-w-[760px] pb-10'}>
        {embedded || fixed ? (
          <div className="flex justify-end px-4 pb-2">
            <Button size="sm" icon={<Plus size={14} />} onClick={() => setAdding(true)}>
              Rule
            </Button>
          </div>
        ) : (
          <PageHead title="Rules & memory">
            <Button size="sm" icon={<Plus size={14} />} onClick={() => setAdding(true)}>
              Rule
            </Button>
          </PageHead>
        )}
        <div
          className="choice px-4 pb-2"
          role="radiogroup"
          aria-label="Scope filter"
          hidden={!!fixed}
        >
          <button
            type="button"
            role="radio"
            aria-checked={!projectId}
            onClick={() => setProjectId(undefined)}
          >
            All
          </button>
          {(projects.data ?? []).map((p) => (
            <button
              key={p.id}
              type="button"
              role="radio"
              aria-checked={projectId === p.id}
              onClick={() => setProjectId(p.id)}
            >
              {p.name}
            </button>
          ))}
        </div>
        {rules.isPending ? <SkeletonRows n={3} /> : null}
        {rules.isError ? (
          <Empty title="Could not load rules">{errorText(rules.error)}</Empty>
        ) : null}
        {rules.isSuccess && !list.length ? (
          <Empty title="No rules yet">
            Rules are short instructions every agent gets (“never touch the release keychain”). The
            weekly retro and agents propose them; you promote the useful ones.
          </Empty>
        ) : null}
        {groups.map(([title, rs]) =>
          rs.length ? (
            <section key={title} aria-label={title}>
              <SectionTitle>
                {title} <span className="text-text-3">{rs.length}</span>
              </SectionTitle>
              <ul className="m-0 list-none p-0">
                {rs.map((r) => (
                  <RuleRow
                    key={r.id}
                    r={r}
                    projectName={projects.data?.find((p) => p.id === r.projectId)?.name}
                  />
                ))}
              </ul>
            </section>
          ) : null,
        )}
      </div>
      {adding ? (
        <NewRule onClose={() => setAdding(false)} {...(fixed ? { projectId: fixed } : {})} />
      ) : null}
    </div>
  )
}

function RuleRow({ r, projectName }: { r: Rule; projectName?: string }) {
  const b = useBackend()
  const qc = useQueryClient()
  const toast = useToast()
  const set = async (status: 'active' | 'retired' | 'proposed', ok: string) => {
    try {
      await b.api.updateRule(r.id, { status })
      await qc.invalidateQueries({ queryKey: ['rules'] })
      await qc.invalidateQueries({ queryKey: ['inbox'] })
      toast(ok)
    } catch (e) {
      toast(errorText(e), 'error')
    }
  }
  return (
    <li className="row !cursor-default">
      <BookMarked size={15} className="mt-0.5 flex-none text-text-3" aria-hidden="true" />
      <span className="grid min-w-0 flex-1 gap-1">
        <span className="text-[15px] leading-5">{r.text}</span>
        <span className="flex flex-wrap items-center gap-1.5 text-[14px] text-text-3">
          <Chip>
            {r.scope === 'global'
              ? 'All projects'
              : r.scope === 'project'
                ? (projectName ?? 'Project')
                : 'Workstream'}
          </Chip>
          <span>from {r.provenance}</span>
          <span aria-hidden="true">·</span>
          {r.lastCitedAt ? (
            <span>
              cited {r.citeCount}× · last <Ago ts={r.lastCitedAt} />
            </span>
          ) : (
            <span>never cited</span>
          )}
        </span>
      </span>
      <span className="flex flex-none gap-1">
        {r.status === 'proposed' ? (
          <>
            <Button
              size="sm"
              variant="ghost"
              icon={<X size={14} />}
              onClick={() => void set('retired', 'Rejected')}
            >
              Reject
            </Button>
            <Button
              size="sm"
              variant="primary"
              icon={<Check size={14} />}
              onClick={() => void set('active', 'Promoted')}
            >
              Promote
            </Button>
          </>
        ) : r.status === 'active' ? (
          <Button size="sm" variant="ghost" onClick={() => void set('retired', 'Retired')}>
            Retire
          </Button>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            icon={<Undo2 size={14} />}
            onClick={() => void set('active', 'Restored')}
          >
            Restore
          </Button>
        )}
      </span>
    </li>
  )
}

function NewRule({ onClose, projectId: fixed }: { onClose: () => void; projectId?: string }) {
  const b = useBackend()
  const qc = useQueryClient()
  const toast = useToast()
  const projects = useProjects()
  const [text, setText] = useState('')
  const [projectId, setProjectId] = useState<string>(fixed ?? '')
  const save = async () => {
    try {
      await b.api.createRule({
        text: text.trim(),
        scope: projectId ? 'project' : 'global',
        ...(projectId ? { projectId } : {}),
      })
      await qc.invalidateQueries({ queryKey: ['rules'] })
      onClose()
    } catch (e) {
      toast(errorText(e), 'error')
    }
  }
  return (
    <Sheet
      open
      onClose={onClose}
      title="New rule"
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!text.trim()} onClick={() => void save()}>
            Add rule
          </Button>
        </>
      }
    >
      <div className="grid gap-3 pb-2">
        <label className="field">
          <span>Rule</span>
          <textarea
            className="input"
            rows={3}
            value={text}
            // biome-ignore lint/a11y/noAutofocus: primary input of the sheet
            autoFocus
            placeholder="Run the iOS simulator tests before landing UI changes"
            onChange={(e) => setText(e.target.value)}
          />
        </label>
        <label className="field">
          <span>Applies to</span>
          <select
            className="input"
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
          >
            <option value="">All projects</option>
            {(projects.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
      </div>
    </Sheet>
  )
}
